/**
 * MULTI-JURISDICTION P6 — JurisdictionProvider.
 *
 * Resolves the signed-in tenant's jurisdiction config once (React-Query cached),
 * publishes it to the module-singleton in `jurisdiction-config.ts` so the pure
 * `formatCurrency/formatNumber/formatDate/term` helpers pick it up everywhere,
 * and exposes it via `useJurisdiction()` for components that want to react.
 *
 * Flow (all additive, read-only, never blocks render):
 *   1. GET  /tenants/{tenantId}/jurisdiction-profile   (P2 — persisted zones)
 *   2. POST /framework-engine/jurisdictions/resolve      (P0 — registry resolver)
 *      → currency / locale / terminology / regulatory bodies for the PRIMARY zone
 *
 * On any failure it silently keeps the CA-QC fallback (zero regression). Must be
 * mounted INSIDE AuthProvider (uses the tenant id from the JWT).
 */
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../api/client'
import { resolveApiUrl } from '../api/config'
import { useTenantId } from '../auth/useAuth'
import {
  JurisdictionConfig,
  FALLBACK_JURISDICTION,
  setActiveJurisdiction,
} from './jurisdiction-config'

interface JurisdictionContextValue {
  config: JurisdictionConfig
  isLoading: boolean
  isFallback: boolean
  refetch: () => void
}

const JurisdictionContext = createContext<JurisdictionContextValue>({
  config: FALLBACK_JURISDICTION,
  isLoading: false,
  isFallback: true,
  refetch: () => {},
})

async function fetchJurisdictionConfig(tenantId: string): Promise<JurisdictionConfig> {
  // 1) Tenant's assigned zones (P2).
  let jurisdictions: string[] = []
  let primary = ''
  try {
    const prof = await apiClient.get(resolveApiUrl(`/tenants/${tenantId}/jurisdiction-profile`))
    const data = prof?.data ?? {}
    jurisdictions = Array.isArray(data.jurisdictions) ? data.jurisdictions : []
    primary = data.primary_jurisdiction || data.primary || jurisdictions[0] || ''
  } catch {
    // No profile yet → fall through to the CA-QC default below.
  }
  if (!primary) return FALLBACK_JURISDICTION

  // 2) Resolve currency/locale/terminology/bodies for the primary zone (P0).
  const res = await apiClient.post(resolveApiUrl('/framework-engine/jurisdictions/resolve'), {
    jurisdictions: [primary],
  })
  const d = res?.data ?? {}
  const cfg = d.config ?? {}
  return {
    jurisdictions: jurisdictions.length ? jurisdictions : [primary],
    primary,
    currency: d.currency || cfg.currency_code || FALLBACK_JURISDICTION.currency,
    locale: d.locale || cfg.default_locale || FALLBACK_JURISDICTION.locale,
    dateFormat: cfg.date_format,
    numberFormat: cfg.number_format,
    terminology: d.terminology || cfg.terminology || {},
    bodies: Array.isArray(d.regulatoryBodies)
      ? d.regulatoryBodies.map((b: any) => ({ code: b.code, name: b.name }))
      : [],
  }
}

export function JurisdictionProvider({ children }: { children: React.ReactNode }) {
  const tenantId = useTenantId()
  const enabled = !!tenantId && tenantId !== 'default-tenant'

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['jurisdiction-config', tenantId],
    queryFn: () => fetchJurisdictionConfig(tenantId),
    enabled,
    staleTime: 30 * 60 * 1000, // 30 min — jurisdiction rarely changes mid-session
    retry: 1,
  })

  // Publish to the module singleton so the hook-free formatters resolve correctly.
  useEffect(() => {
    if (data) setActiveJurisdiction(data)
  }, [data])

  const value = useMemo<JurisdictionContextValue>(
    () => ({
      config: data ?? FALLBACK_JURISDICTION,
      isLoading,
      isFallback: !data,
      refetch: () => {
        void refetch()
      },
    }),
    [data, isLoading, refetch]
  )

  return <JurisdictionContext.Provider value={value}>{children}</JurisdictionContext.Provider>
}

/** Read the resolved jurisdiction config (currency/locale/terminology/bodies). */
export function useJurisdiction(): JurisdictionContextValue {
  return useContext(JurisdictionContext)
}
